import { ToolBase } from "../../../base/toolBase";
import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import type { SubmitResultsParams } from "../../../base/submitResults.schema";
import { SUBMIT_RESULTS_MARKER, type SubmitResultsInternalResponse } from "../../../base/submitResults.tool";
import { SubmitArtifactTool } from "../submitArtifact";
import type { ArtifactInfo } from "../../linkedExcelEditor.schema";
import {
    LinkedExcelSubmitResultsParamsSchema,
    type LinkedExcelSubmitResultsParams,
    type ArtifactCreationRequest,
} from "./submitResults.schema";

const LINKED_EXCEL_SUBMIT_RESULTS_DESCRIPTION = `Submit final Linked Excel results.

This linked-excel override supports normal submitResults payloads and optional
artifact exports in the same call.

Use artifactsToCreate when you want this tool to export chart/range artifacts
before returning results to the parent agent.

Example:
\`\`\`json
{
  "results": [
    {
      "requirementId": "A",
      "relevantResults": { "changesSummary": "Updated revenue assumptions", "cellsModified": 4 },
      "rationale": "Updated the requested assumptions and verified dependent formulas."
    }
  ],
  "artifactsToCreate": [
    {
      "requirementId": "A",
      "artifact_type": "chart",
      "sheet_name": "Dashboard",
      "chart_name": "Revenue Chart",
      "description": "Updated revenue chart"
    }
  ],
  "summary": "Workbook update complete with export artifact"
}
\`\`\``;

const LINKED_EXCEL_SUBMIT_RESULTS_SYSTEM_PROMPT = `### submitResults
Submit final Linked Excel results. You can optionally auto-create artifacts in this same call.

**When to use:**
- You have completed all edits and verification
- Optionally include artifactsToCreate to export chart/range artifacts before finishing

**IMPORTANT:**
- If exporting artifacts, provide chart_name for chart exports or range_ref for range exports
- With multiple requirements, set requirementId on each artifact so results are attached correctly
- This call ends the agent loop when successful`;

export class LinkedExcelSubmitResultsTool extends ToolBase<LinkedExcelSubmitResultsParams, SubmitResultsInternalResponse> {
    public readonly name = "submitResults";
    public readonly description: ToolDescription;
    public readonly systemPromptDescription = LINKED_EXCEL_SUBMIT_RESULTS_SYSTEM_PROMPT;

    private readonly submitArtifactTool: SubmitArtifactTool;

    constructor(submitArtifactTool: SubmitArtifactTool) {
        super();
        this.submitArtifactTool = submitArtifactTool;
        this.description = new ToolDescription(
            "submitResults",
            LINKED_EXCEL_SUBMIT_RESULTS_DESCRIPTION,
            LinkedExcelSubmitResultsParamsSchema
        );
    }

    /**
     * Forward the active Excel session to artifact creation.
     */
    public setSessionId(sessionId: string): void {
        this.submitArtifactTool.setSessionId(sessionId);
    }

    public async execute(parameters: unknown): Promise<SubmitResultsInternalResponse> {
        const parseResult = safeParseToolParams(LinkedExcelSubmitResultsParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(parseResult.error);
        }

        const params = parseResult.data;
        const enrichedResults = params.results.map((result) => ({
            ...result,
            relevantResults: { ...(result.relevantResults || {}) },
        }));

        const createdArtifactIds: string[] = [];
        const indexByRequirementId = new Map<string, number>();
        for (let i = 0; i < enrichedResults.length; i++) {
            indexByRequirementId.set(enrichedResults[i].requirementId, i);
        }

        for (const artifactRequest of params.artifactsToCreate || []) {
            const targetRequirementId = this.resolveTargetRequirementId(artifactRequest, enrichedResults);
            const resultIndex = indexByRequirementId.get(targetRequirementId);
            if (resultIndex === undefined) {
                throw new Error(
                    `Artifact request references unknown requirementId '${targetRequirementId}'.`
                );
            }

            const artifactInfo = await this.createArtifact(artifactRequest);
            createdArtifactIds.push(artifactInfo.artifactId);

            const targetRelevantResults = enrichedResults[resultIndex].relevantResults as Record<string, unknown>;
            const existingArtifacts = Array.isArray(targetRelevantResults.artifacts)
                ? (targetRelevantResults.artifacts as ArtifactInfo[])
                : [];
            targetRelevantResults.artifacts = [...existingArtifacts, artifactInfo];
        }

        const submittedResults: SubmitResultsParams = {
            results: enrichedResults,
            summary: params.summary,
            notes: this.buildNotes(params.notes, createdArtifactIds),
        };

        const artifactMsg = createdArtifactIds.length > 0
            ? ` and ${createdArtifactIds.length} artifact(s)`
            : "";

        return {
            accepted: true,
            message: `Successfully submitted ${params.results.length} result(s)${artifactMsg}`,
            [SUBMIT_RESULTS_MARKER]: true,
            submittedResults,
        };
    }

    private resolveTargetRequirementId(
        request: ArtifactCreationRequest,
        results: LinkedExcelSubmitResultsParams["results"]
    ): string {
        if (request.requirementId) return request.requirementId;
        if (results.length === 1) return results[0].requirementId;
        throw new Error("artifact.requirementId is required when submitting multiple requirements.");
    }

    private async createArtifact(request: ArtifactCreationRequest): Promise<ArtifactInfo> {
        const artifactLocator =
            request.artifact_type === "chart"
                ? { chart_name: request.chart_name }
                : { range_ref: request.range_ref };

        const response = await this.submitArtifactTool.execute({
            artifact_type: request.artifact_type,
            sheet_name: request.sheet_name,
            ...artifactLocator,
            description: request.description,
        });

        if (!response.success) {
            throw new Error(response.error || "Artifact creation failed.");
        }

        return {
            artifactId: response.artifact_id,
            description: response.description,
            artifactType: response.artifact_type,
            sourceSheet: response.source.sheet,
            sourceRef: response.source.ref,
        };
    }

    private buildNotes(existingNotes: string | undefined, createdArtifactIds: string[]): string | undefined {
        if (createdArtifactIds.length === 0) return existingNotes;
        const artifactNote = `Created artifacts: ${createdArtifactIds.join(", ")}`;
        if (!existingNotes) return artifactNote;
        return `${existingNotes}\n${artifactNote}`;
    }
}

export default LinkedExcelSubmitResultsTool;
